import { InjectionToken, Signal } from '@angular/core';
import moment from 'moment';
import { Observable } from 'rxjs';
import { NOTIFICATION_STATUS_CODE } from '../enums/notification-status-code.enum';
import { AddressModel } from '../models/complex/address.model';
import { TaskModel } from '../models/complex/task.model';
import { NotificationHistoryModel } from '../models/low-level/notification-history.model';
import { NotificationIdentifierModel } from '../models/low-level/notification-identifier.model';
import { NotificationModel } from '../models/low-level/notification.model';
import { StatusModel } from '../models/low-level/status.model';
import { Equipment } from '../types/equipment';
import { NotificationInterface } from './notification.interface';

/**
 * Public contract of the notification service: the in-memory notification list, the current
 * per-notification status signal, and the getters/mutators that components and services use to
 * read notifications and drive their status transitions.
 *
 * Extends {@link NotificationInterface}, the narrower contract already implemented by the
 * standalone forms apps' own `NotificationService` classes — the legacy `NotificationService`
 * satisfies both.
 *
 * Implemented by both the legacy `NotificationService` (in-memory, OData-backed) and (later) the
 * NgRx-backed `NotificationNgrxService`; consumers depend on this interface via
 * {@link NotificationDataInstanceToken} rather than on a concrete class.
 */
export interface NotificationDataInterface extends NotificationInterface {
    readonly isInitialized: boolean;

    // used for material-return screen
    readonly customerAddresses: AddressModel[];

    readonly notificationStatusSignal: Signal<StatusModel>;

    init(isAppStart?: boolean): Observable<NotificationModel[]>;

    getStatus(notification: NotificationModel): StatusModel;

    getNotificationStatusByStatusType(
        notification: NotificationModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): StatusModel | undefined;

    getNotificationTaskByStatusType(
        notification: NotificationModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): TaskModel | undefined;

    saveStatus(notification: NotificationModel, status: StatusModel): Observable<void>;

    getNotificationStart(notification: NotificationModel): moment.Moment;

    getNotificationEnd(notification: NotificationModel): moment.Moment;

    isNotificationRemoteWork(notification: NotificationModel): boolean;

    isMultiDayNotification(notification: NotificationModel): boolean;

    isNotificationInProgress(notificationIdentifier: NotificationIdentifierModel): boolean;

    isNotificationCancelled(notification: NotificationModel): boolean;

    getEscalated(notification: NotificationModel): boolean;
    saveEscalated(notification: NotificationModel, escalated: boolean): Observable<void>;

    getAllNotifications(): NotificationModel[];

    getUpcomingNotifications(): NotificationModel[];

    getNewNotifications(): NotificationModel[];

    getTodaysNotifications(): NotificationModel[];

    isTodaysNotification(notification: NotificationModel): boolean;

    getNotificationInProgress(): NotificationModel | undefined;

    getAllNotificationsInProgress(): NotificationModel[];

    getAllNotificationsInEtaStatus(): NotificationModel[];

    getNotificationToBeSigned(): NotificationModel | undefined;

    getAllNotificationsToBeSigned(): NotificationModel[];

    displayInstallationPaperwork(notificationId: NotificationIdentifierModel): boolean;

    isAnyOtherNotificationInStatusDisp(currentNotification: NotificationModel): boolean;

    getNextUpcomingNotification(mainNotification: NotificationModel): NotificationModel | undefined;

    getCancelled(notification: NotificationModel): string;
    saveCancelled(notification: NotificationModel, statusCode: NOTIFICATION_STATUS_CODE): void;

    getAvailableStatus(notification: NotificationModel): NOTIFICATION_STATUS_CODE[];

    getNotificationByEquipment(equipmentId: string): NotificationModel[];

    getNotificationsEquipments(): Equipment[];

    sortNotificationsByStartDate(notifications: NotificationModel[]): NotificationModel[];

    getUnreadIndicator(notification: NotificationModel): boolean;
    saveUnreadIndicator(notification: NotificationModel, unread: boolean): Observable<void>;

    getPausedIndicator(notification: NotificationModel): boolean;
    savePausedIndicator(notificationList: NotificationModel[], paused: boolean): Observable<void[]>;

    saveNotificationStatusModelList(): Observable<void>;

    saveCancellationConfirmationForInprogress(identifier: NotificationIdentifierModel): Observable<unknown>;
    removeCancellationConfirmationForInprogress(): void;
    getCancellationConfirmationForInprogress(id: NotificationIdentifierModel): Promise<boolean>;

    initializeNotificationStatus(notification: NotificationModel): void;

    handlePausedNotifications(): Observable<void[] | undefined>;

    showNoEquipmentNotificationList(showAppNotification: boolean): void;

    setNotificationProperties(notification: NotificationModel): void;

    synchronizeNotificationsCrossDevice(): Observable<NotificationModel[]>;

    isNotificationInProgressAndCancelledAsync(notification: NotificationModel): Promise<boolean>;

    getNotificationsHistory(): Observable<NotificationHistoryModel[]>;
}

/**
 * DI token for {@link NotificationDataInterface}. Lets the app choose which implementation to
 * provide (legacy `NotificationService` or the NgRx-based `NotificationNgrxService`) without
 * consumers depending on a concrete class.
 */
export const NotificationDataInstanceToken = new InjectionToken<NotificationDataInterface>('NotificationDataInterface');
